import {RsnButton, RsnDialog, cn} from '@raison/ui-kit';
import {useQuery} from '@tanstack/react-query';
import React, {Fragment} from 'react';
import {Country, useCountriesQueryOptions} from '~/entities/countries';
import {useCurrencyFormat, usePrimaryCurrencyFormat, useRateToPrimaryCurrency} from '~/entities/currencies';
import {TrTx} from '../model/TrTx';
import {TxParticipantType} from '../model/TrTxParticipant';
import {CalculatedAmounts} from './SendTxIntro';

export function TrtDetailsDialog({trt, amounts}: {trt: TrTx; amounts: CalculatedAmounts | null}) {
    const rate = useRateToPrimaryCurrency(trt.currency);
    const {format: formatAsPrimary} = usePrimaryCurrencyFormat({approx: true});
    const {format: formatAsSubject} = useCurrencyFormat(trt.currency);
    const {format: formatAsSubjectBlockchain} = useCurrencyFormat(trt.fromAccount.blockchain);

    const {data: countriesList = []} = useQuery({
        ...useCountriesQueryOptions(),
    });

    const renderDetail = renderDetailWith(countriesList);

    return (
        <RsnDialog.Window>
            <RsnDialog.CenteredHeader>{t.OperationDetails}</RsnDialog.CenteredHeader>
            <RsnDialog.Body>
                <dl className={cn(cls.surface, cls.dataList)}>
                    <dl>
                        {renderDataEntry(t.FromAccount, trt.fromAccount.name)}
                        {renderDataEntry(t.ToAddress, trt.toAddress, {breakAll: true})}
                        {renderDataEntry(t.Details, trt.comment ?? EM_DASH)}
                    </dl>
                    <dl>{trt.additionalDetails.map(renderDetail)}</dl>
                    {amounts ? renderCalculatedAmountDetails(amounts) : renderTrtAmountDetails()}
                </dl>
            </RsnDialog.Body>
            <RsnDialog.Footer>
                <RsnButton.Alike intent='secondary' as={RsnDialog.CloseTrigger}>
                    {t.IGotIt}
                </RsnButton.Alike>
            </RsnDialog.Footer>
        </RsnDialog.Window>
    );

    function renderCalculatedAmountDetails(amounts: CalculatedAmounts) {
        return (
            <dl>
                {renderDataEntry(t.TransactionFee, formatAsSubjectBlockchain(amounts.feeAmount))}
                {renderDataEntry(t.YouSpend, formatAsSubject(amounts.amountToBeSent))}
                {renderDataEntry(t.ApproxValue, formatAsPrimary(amounts.amountToBeSent.multipliedBy(rate)))}
                {renderDataEntry(t.BeneficiaryGets, formatAsSubject(amounts.amountToBeReceived))}
            </dl>
        );
    }

    function renderTrtAmountDetails() {
        return (
            <dl>
                {renderDataEntry(t.YouSpend, formatAsSubject(trt.amount))}
                {renderDataEntry(t.ApproxValue, formatAsPrimary(trt.amount.multipliedBy(rate)))}
            </dl>
        );
    }
}

const EM_DASH = '—';

const renderDataEntry = (
    keyLabel: string,
    valueText: string,
    {breakAll = false}: Partial<{breakAll: boolean}> = {}
) => (
    <Fragment>
        <dt>{keyLabel}</dt>
        <dd className={cn(breakAll ? 'break-all' : '')}>{valueText}</dd>
    </Fragment>
);

const renderDetailWith = (countriesList: readonly Country[]) => (entry: {name: string; value: string}) => {
    const {name: key, value} = entry;
    const render = (keyLabel: string, valueText: string) => (
        <Fragment key={key}>
            <dt>{keyLabel}</dt>
            <dd>{valueText}</dd>
        </Fragment>
    );

    switch (key) {
        case 'beneficiary_type': {
            const textMap: Record<string, string> = {
                [TxParticipantType.self]: 'Myself',
                [TxParticipantType.individual]: 'Third-party individual',
                [TxParticipantType.company]: 'Third-party company',
            };
            const text = textMap[value];
            if (!text) {
                return null;
            }
            return render(t.WhoImSendingTo, text);
        }
        case 'beneficiary_name':
            return render(t.FullName, value);
        case 'beneficiary_email':
            return render(t.Email, maskEmail(value));
        case 'beneficiary_country': {
            const country = countriesList.find(c => c.alpha3 === value);
            if (!country) {
                return null;
            }
            return render(t.Country, country.name);
        }
        case 'beneficiary_wallet_vasp':
            return render(t.WalletVASP, value);
        case 'beneficiary_wallet_type': {
            const walletTypeMap: Record<string, string> = {vasp: 'VASP', unhosted: 'Unhosted'};
            const walletTypeText = walletTypeMap[value] ?? value;
            return render(t.WalletType, walletTypeText);
        }
        case 'beneficiary_lei':
            return render(t.LEI, value);
        case 'beneficiary_crn    ':
            return render(t.CRN, value);
    }
};

const maskEmail = (email: string) => {
    const indexOfAt = email.indexOf('@');
    const unmasked = email.slice(0, Math.min(indexOfAt, 2));
    const atDomain = email.slice(indexOfAt);
    return `${unmasked}***${atDomain}`;
};

const cls = {
    surface: 'fv-text-m-regular flex flex-col gap-4 rounded-lg bg-neutral-20 p-5',
    dataList: cn(
        '[&_dl]:grid [&_dl]:grid-cols-[auto_1fr] [&_dl]:gap-2',
        '[&_dt]:col-start-1 [&_dt]:text-left [&_dt]:text-neutral-60',
        '[&_dd]:col-start-2 [&_dd]:text-right [&_dd]:text-neutral-100'
    ),
};

const t = {
    FromAccount: 'From account',
    ToAddress: 'To address',
    Details: 'Details',
    WhoImSendingTo: 'Who I’m sending to',
    FullName: 'Full name',
    Email: 'Email',
    LEI: 'LEI',
    CRN: 'CRN',
    Country: 'Country',
    WalletType: 'Wallet type',
    WalletVASP: 'Wallet VASP',
    IGotIt: 'I got it',
    OperationDetails: 'Operation details',

    TransactionFee: 'Transaction fee',
    YouSpend: 'You spend',
    ApproxValue: 'Approx value',
    BeneficiaryGets: 'Beneficiary gets',
};
